import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { ResolvedNavItem, ResolvedNavSection } from "./resolve-nav-items";

/**
 * ナビの描画のみ。表示する／しないの判定は resolveNavSections が済ませている。
 */
export function AppSidebarNav({
  sections,
  onNavigate,
}: {
  sections: readonly ResolvedNavSection[];
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("nav");

  return (
    <nav aria-label={t("sectionBusiness")} className="flex flex-col gap-6 p-3">
      {sections.map((section) => (
        <div key={section.titleKey} className="flex flex-col gap-1">
          <p className="text-muted-foreground px-2 pb-1 text-xs font-medium">
            {t(section.titleKey)}
          </p>
          {section.items.map((item) => (
            <NavItemLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  );
}

function NavItemLink({
  item,
  onNavigate,
}: {
  item: ResolvedNavItem;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("nav");
  const Icon = item.icon;
  const label = t(`items.${item.key}` as "items.home");

  const baseClass =
    "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors";

  if (item.disabled || item.to === undefined) {
    return (
      <span
        aria-disabled="true"
        className={cn(baseClass, "text-muted-foreground/70 cursor-not-allowed")}
      >
        <Icon aria-hidden="true" className="size-4 shrink-0" />
        <span className="truncate">{label}</span>
        <Badge variant="outline" className="ml-auto text-[10px]">
          {t("plannedBadge")}
        </Badge>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          baseClass,
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
            : "text-sidebar-foreground hover:bg-sidebar-accent/60",
        )
      }
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
